import { evaluationGridService } from '@/services/evaluation-grid.service';
import axios from 'axios';
import { sortEvaluationGridFields } from '@/util/evaluation-grid-util';

const initialState = () => ({
  evaluationGrid: null,
  notFound: false,
  status: {
    error: null,
    pending: true,
    resolved: false,
    rejected: false,
  },
});

const state = initialState();

const actions = {
  getEvaluationGrid({ commit }, opportunityId) {
    commit('GET_EVALUATION_GRID');
    return evaluationGridService.getEvaluationGrid({ opportunityId }).then(
      (evaluationGrid) => {
        commit('GET_EVALUATION_GRID_SUCCESS', evaluationGrid);
        commit('annotations/SET_LINKED_ANNOTATIONS', evaluationGrid, { root: true });
        return evaluationGrid;
      },
      (error) => {
        if (axios.isCancel(error)) return;
        if (error.response && error.response.status === 404) {
          commit('GET_EVALUATION_GRID_NOT_FOUND');
          commit('annotations/SET_LINKED_ANNOTATIONS', { fields: [] }, { root: true });
          return;
        }
        commit('GET_EVALUATION_GRID_ERROR', error);
        throw error;
      },
    );
  },
  resetEvaluationGrid({ commit }) {
    commit('RESET');
    commit('annotations/SET_LINKED_ANNOTATIONS', { fields: [] }, { root: true });
  },
  addEvalGridValue(
    { commit },
    { opportunityId, fieldId, source, textAnnotationId, opportunityDocumentId, value, valueType },
  ) {
    return evaluationGridService
      .addEvalGridValue({
        opportunityId,
        fieldId,
        source,
        textAnnotationId,
        opportunityDocumentId,
        value,
        valueType,
      })
      .then(
        (createdValue) => {
          commit('ADD_VALUE_SUCCESS', { fieldId, createdValue });
          if (source === 'PDF_ANNOTATION') {
            commit(
              'annotations/ADD_LINKED_ANNOTATION',
              {
                textAnnotationId,
                value: createdValue,
              },
              { root: true },
            );
          }
        },
        (error) => {
          throw error;
        },
      );
  },
  updateEvalGridValue({ commit }, { opportunityId, fieldId, id, value, valueType }) {
    return evaluationGridService.updateEvalGridValue({ opportunityId, fieldId, id, valueType });
  },
  deleteEvalGridValue({ commit }, { opportunityId, fieldId, value }) {
    const id = value.id;
    return evaluationGridService.deleteEvalGridValue({ opportunityId, fieldId, id }).then(
      () => {
        commit('DELETE_VALUE_SUCCESS', { fieldId, id });
        if (value.textAnnotationId && value.source === 'PDF_ANNOTATION') {
          commit(
            'annotations/DELETE_LINKED_ANNOTATION',
            { textAnnotationId: value.textAnnotationId, value },
            { root: true },
          );
        }
      },
      (error) => {
        throw error;
      },
    );
  },
  uploadEvalGridValueImage({ commit }, { opportunityId, fieldId, formData }) {
    return evaluationGridService
      .uploadEvalGridValueImage({
        opportunityId,
        fieldId,
        formData,
      })
      .then(
        (createdValues) => {
          // returns an array (can upload multiple images)
          commit('ADD_VALUES_SUCCESS', { fieldId, createdValues });
        },
        (error) => {
          commit('UPLOAD_EVAL_GRID_IMAGE_ERROR', error);
          throw error;
        },
      );
  },
  addEvalGridField({ commit }, { opportunityId, field }) {
    commit('ADD_NEW_FIELD');
    return evaluationGridService.addEvalGridField({ opportunityId, field }).then(
      () => {
        commit('ADD_NEW_FIELD_SUCCESS');
      },
      (error) => {
        commit('ADD_NEW_FIELD_ERROR', error);
        throw error;
      },
    );
  },
  updateEvalGridField({ commit }, { opportunityId, fieldId, newField }) {
    commit('UPDATE_FIELD');
    return evaluationGridService.updateEvalGridField({ opportunityId, fieldId, newField }).then(
      () => {
        commit('UPDATE_FIELD_SUCCESS');
      },
      (error) => {
        commit('UPDATE_FIELD_ERROR', error);
        throw error;
      },
    );
  },
  deleteEvalGridField({ commit }, { opportunityId, fieldId }) {
    commit('DELETE_FIELD');
    return evaluationGridService.deleteEvalGridField({ opportunityId, fieldId }).then(
      () => {
        commit('DELETE_FIELD_SUCCESS');
      },
      (error) => {
        commit('DELETE_FIELD_ERROR', error);
        throw error;
      },
    );
  },
};

const mutations = {
  GET_EVALUATION_GRID(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
    // Keep current grid while refreshing. Callers should reset when switching opportunities.
    state.notFound = false;
  },
  GET_EVALUATION_GRID_ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
  },
  GET_EVALUATION_GRID_NOT_FOUND(state) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    state.evaluationGrid = null;
    state.notFound = true;
  },
  GET_EVALUATION_GRID_SUCCESS(state, evaluationGrid) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    state.notFound = false;

    const modifiedFields = evaluationGrid.fields.map((field) => {
      if (field.type !== 'SELECT') {
        return {
          ...field,
          buttons: [
            { name: 'key', icon: 'key', selected: false },
            { name: 'value', icon: 'database', selected: false },
          ],
        };
      } else {
        return field;
      }
    });

    state.evaluationGrid = {
      ...evaluationGrid,
      fields: modifiedFields,
    };
  },
  ADD_VALUE_SUCCESS(state, { fieldId, createdValue }) {
    const fieldIndex = state.evaluationGrid.fields.findIndex((field) => field.id === fieldId);
    state.evaluationGrid.fields[fieldIndex].values.push(createdValue);
  },
  ADD_VALUES_SUCCESS(state, { fieldId, createdValues }) {
    const fieldIndex = state.evaluationGrid.fields.findIndex((field) => field.id === fieldId);
    state.evaluationGrid.fields[fieldIndex].values.push(createdValues);
  },
  DELETE_VALUE_SUCCESS(state, { fieldId, id }) {
    const fieldIndex = state.evaluationGrid.fields.findIndex((field) => field.id === fieldId);
    const newValues = state.evaluationGrid.fields[fieldIndex].values.filter((value) => value.id !== id);

    state.evaluationGrid.fields[fieldIndex].values = newValues;
  },
  ADD_NEW_FIELD(state) {
    state.status = {
      ...state.status,
      pending: true,
      resolved: false,
    };
  },
  ADD_NEW_FIELD_SUCCESS(state) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
  },
  ADD_NEW_FIELD_ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
  },
  UPDATE_FIELD(state) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
  },
  UPDATE_FIELD_SUCCESS(state) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
  },
  UPDATE_FIELD_ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
  },
  DELETE_FIELD(state) {
    state.status = {
      ...state.status,
      pending: true,
      resolved: false,
    };
  },
  DELETE_FIELD_SUCCESS(state) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
  },
  DELETE_FIELD_ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
  },
  UPLOAD_EVAL_GRID_IMAGE_ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
  },
  RESET(state) {
    const initial = initialState();
    Object.keys(initial).forEach((key) => {
      state[key] = initial[key];
    });
  },
};

const getters = {
  evaluationGrid: (state) => state.evaluationGrid,
  evaluationFields: (state) =>
    state.evaluationGrid && state.evaluationGrid.fields ? sortEvaluationGridFields(state.evaluationGrid).fields : [],
  notFound: (state) => state.notFound,
  // status
  error: (state) => state.status.error,
  pending: (state) => state.status.pending,
  rejected: (state) => state.status.rejected,
  resolved: (state) => state.status.resolved,
};

export const evaluationGrid = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
